import { z } from "zod";
const featureFlagDefinitions = {
  AgentsTab: {
    value: "agents-tab",
    description: "Makes the Agents tab visible in the navigation header top bar"
  },
  AutomatedOutreach: {
    value: "automated-outreach",
    description: "When enabled, pending participants (created via CSV or the API) will automatically be sent an SMS invite to take their interview."
  },
  AutomationReportEmailContent: {
    value: "automation-report-email-content",
    description: "Embeds screened and redacted automation report content in notification emails for companies whose link-only report email setting is disabled; while off, their emails remain link-only."
  },
  InterviewSections: {
    value: "interview-sections",
    description: "Shows interview sections cards in the job type slideover and allows sections to be added. * "
  },
  KnowledgeBase: {
    value: "knowledge-base",
    description: `Enable the display of the "Knowledge Bases" section in the settings as well as the "Knowledge bases" dropdown in the playbook editor for teammate performance knowledge base adherence scoring. Note: "Knowledge bases" and KB adherence scoring is always available/enabled for voice agents.`
  },
  HideHireTab: {
    value: "hide-hire-tab",
    description: "Despite the name, hides the whole recruiting surface for a company, not just the Hire tab: also the recruiting-flavoured settings sections and the Interview resource types. The inverse of the legacy PostHog `recruiting` flag, which was on for everyone but an exclusion list \u2014 naming the exclusion directly means companies created later keep recruiting without anyone adding a row."
  },
  Scheduling: {
    value: "scheduling",
    description: ""
  },
  TeamPerformanceTab: {
    value: "team-performance-tab",
    description: "Display the Team performance tab in the top navigation bar."
  },
  Training: {
    value: "training",
    description: ""
  },
  Reports: {
    value: "reports",
    description: ""
  },
  PerformanceReport: {
    value: "performance-report",
    description: "Show performance report card on reports page"
  },
  InsuranceReports: {
    value: "insurance-reports",
    description: "Show insurance upsell reports on reports page"
  },
  ReportsResearchAgent: {
    value: "reports-research-agent",
    description: "Enables the Copilot panel on the reports page (and hides the bottom-bar variant while it's visible)."
  },
  Topics: {
    value: "topics",
    description: "Shows the Topics view in the performance workspace, extracts interaction moments from each grading, and includes the company in the weekly per-topic analysis job. Signals are released to everyone and are not gated by this flag."
  },
  VocalIntelligence: {
    value: "vocal-intelligence",
    description: "When enabled, surfaces the vocal intelligence radar chart (authority/energy/rapport, scored 1-5) on the teammate performance overview."
  },
  CustomerExperienceScore: {
    value: "customer-experience-score",
    description: "When enabled, surfaces customer experience score values on the frontend \u2014 the per-grading 1-5 pill in the grading assessment view and gradings tables, and the per-teammate 0-100 average column in the team performance tables."
  },
  CustomerProfile: {
    value: "customer-profile",
    description: "Shows the Customers section in the Performance workspace and allows viewing its customer list."
  },
  FunnelOutcome: {
    value: "funnel-outcome",
    description: "Exposes the Funnel outcome type in the playbook outcome type picker. Playbooks that already have a Funnel outcome saved continue to show it regardless of this flag."
  },
  ZoomCcIntegration: {
    value: "zoom-cc-integration",
    description: "Shows the Zoom Contact Center card in Settings \u2192 Integrations, letting an admin connect, pause, and disconnect the call-recording sync themselves. Off by default: only contact-centre customers on Zoom should see it."
  },
  MicrosoftTeams: {
    value: "microsoft-teams",
    description: "Shows the Microsoft Teams surfaces in the admin app: the Teams card in Settings \u2192 Integrations and the Microsoft Teams delivery channel option in agent notification settings."
  },
  OutboundVoiceAgents: {
    value: "outbound-voice-agents",
    description: "Lets a company schedule outbound calls for its voice agents through the public Call Task API (POST /api/v1/call-tasks and friends). Off means the endpoints are rejected as forbidden."
  },
  AgentManagedOutreach: {
    value: "agent-managed-outreach",
    description: "Shows the 'Agent-managed outreach' toggle in an agent's Outbound calls settings."
  },
  NewBilling: {
    value: "new-billing",
    description: "Replaces the Billing settings page with the redesigned plan-summary + AI credits usage page."
  },
  BasetenGrading: {
    value: "baseten-grading",
    description: "Grades newly enqueued teammate/account assessments (e.g. recordings ingested via the public API) with Baseten as the inference provider, routed through the dedicated Baseten grading queue and worker. HQ regrade tooling defaults its inference mode from this flag but can override it."
  },
  NearDuplicateRecordingCheck: {
    value: "near-duplicate-recording-check",
    description: "Fingerprints every recording a company uploads through the public API and its intake integrations, indexes it, and logs the closest existing recording (a near-duplicate check, in log-only mode). Nothing is deduplicated by this flag alone; turn it on and backfill the company's history before considering the dedup flag. Off stops every part of the feature for the company. NOT TESTED AT HIGH VOLUME: before the recording is created, the upload request awaits a fingerprint Lambda (deadline 45 s for audio, 180 s for video), a ClickHouse ranking query and a Postgres recent-band read, then serialises on a per-company advisory lock behind a four-permit in-process gate. Every stage degrades to creating the recording as it does today when it fails or times out, but the latency is added to every upload and the lock makes a company's concurrent uploads wait on each other, so enabling this for a high-volume customer risks recording upload API calls timing out."
  },
  NearDuplicateRecordingDedup: {
    value: "near-duplicate-recording-dedup",
    description: "Resolves an upload whose audio near-matches an existing recording of the same teammate to that recording instead of creating a new one, the way a byte-identical re-upload already does. Implies the check flag. Enable only after the check flag's log-only period shows the expected match rate for the company. Adds no work to the upload: the fingerprint, the queries and the per-company lock all belong to the check flag and are taken whenever it is on. What this changes is the outcome, so a false match resolves an upload to the wrong recording instead of merely logging it."
  },
  TeammatePlaybookOpportunities: {
    value: "teammate-playbook-opportunities",
    description: "Highlights and filters the current week's goal and opportunity criteria in the teammate playbook table and compares scores with the team median there. The slideover layout and team example cards are available to everyone. Enables independent weekly opportunity generation, stored separately from weekly coaching goals."
  },
  TeammateCoachingTakeaways: {
    value: "teammate-coaching-takeaways",
    description: "Generates a short coaching takeaway per teammate and criterion for each saved weekly coaching opportunity, and shows the latest one in the criterion slideover. Requires teammate-playbook-opportunities, since takeaway jobs are queued by the opportunities job. Up to three agent jobs per active teammate per week; measure cost on one small company before widening."
  },
  EqualCriterionPlaybookScore: {
    value: "equal-criterion-playbook-score",
    description: "Rolls criteria up into teammate playbook and section scores by averaging each criterion over the interactions it applies to and then averaging the criteria equally, instead of averaging per-interaction scores. Affects every surface that shows a playbook or section score over more than one interaction (dashboards, reports, trends, emails, agent tools); single-interaction scores are unchanged. Safe to turn off again at any time."
  },
  RetellWebCallV3: {
    value: "retell-web-call-v3",
    description: "Creates participant web calls (interviews and course lessons) through Retell's /v3/create-web-call, which connects the browser over Retell's gateway transport, instead of the v2 endpoint Retell retires on 2026-09-30. Off keeps the v2 endpoint and LiveKit transport."
  },
  CodeTools: {
    value: "code-tools",
    description: "Lets the company create and save code tools: voice agent tools written as a JavaScript script that runs in the code-tool sandbox and may call the domains the tool allows, with secrets stored encrypted per tool."
  },
  RecordingProcessingViaAbsurd: {
    value: "recording-processing-via-absurd",
    description: "Runs the assessment of the company's uploaded recordings (public API /upload and /upload-video, and the S3, Genesys and Zoom CC intake that share their import code) on the Absurd recording-processing worker instead of the interview-processing Lambda; the grading and other follow-up jobs stay on SQS. Transcript-only uploads stay on the Lambda. Recordings recorded more than a day ago go to the backfill queue, as they do on SQS. Unlike SQS, recordings of the same teammate and account are no longer processed one at a time; their grading still is. Only affects recordings imported after it is turned on; safe to turn off again at any time, a task already queued still runs."
  }
};
const featureFlagKeySchema = z.string().min(1).max(100).regex(
  /^[a-z0-9]+(-[a-z0-9]+)*$/,
  "Feature flag keys are lower-case kebab-case"
).brand();
function asFeatureFlagKey(key) {
  return featureFlagKeySchema.parse(key);
}
const archivedFeatureFlagValueDefinitions = {
  "agent-automations-demo": true,
  "agent-teammate-coaching": true,
  "customer-interaction-splitting": true,
  "customer-interactions-contact-name-filter": true,
  "display-sample-tool-health-check-results": true,
  "durable-outbound-calls": true,
  forms: true,
  "genesys-integration": true,
  "live-coaching": false,
  "live-coaching-preview": false,
  "playbook-feedback-review-tab": true,
  "redact-ssn": true,
  "split-coaching-pipeline": false
};
const archivedFeatureFlagValues = new Map(
  Object.entries(archivedFeatureFlagValueDefinitions).map(([key, value]) => [
    asFeatureFlagKey(key),
    value
  ])
);
function getArchivedFeatureFlagValue(key) {
  return archivedFeatureFlagValues.get(key);
}
function isFeatureFlagArchived(key) {
  return archivedFeatureFlagValues.has(key);
}
const FeatureFlagKey = Object.fromEntries(
  Object.entries(featureFlagDefinitions).map(([name, { value }]) => [
    name,
    asFeatureFlagKey(value)
  ])
);
const featureFlagDescriptions = new Map(
  Object.values(featureFlagDefinitions).map((definition) => [
    asFeatureFlagKey(definition.value),
    definition.description
  ])
);
export {
  FeatureFlagKey,
  archivedFeatureFlagValues,
  asFeatureFlagKey,
  featureFlagDescriptions,
  featureFlagKeySchema,
  getArchivedFeatureFlagValue,
  isFeatureFlagArchived
};
//# sourceMappingURL=keys.js.map
